import React, { useEffect, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import Skeleton from 'react-loading-skeleton'

import { httpOk, GET } from 'helpers/api'
import { formatDate } from 'helpers/helpers'
import {
	Card, EmptyNote, baseChartOptions, formatAmount, formatNumber, mutedStyle,
} from '../admin/analytics/parts/common'

const COLOR_IN = '#1f9d57'
const COLOR_OUT = '#d23434'
const COLOR_MAIN = '#4c8df0'

// Текущий месяц неполный — его столбец бледнее, чтобы провал не читался как падение
const PARTIAL_OPACITY = 0.4

const PERIODS = [3, 6, 12]

const STATUS_LABELS = {
	1: { text: 'активна, без чеков', color: 'var(--dark-fg-warning, #b7791f)', background: 'var(--dark-tint-warning, #fff6e5)' },
	[-1]: { text: 'заблокирована', color: 'var(--dark-fg-danger, #d23434)', background: 'var(--dark-tint-danger, #fdecec)' },
	[-3]: { text: 'удалена', color: 'var(--dark-text-muted, #6b7280)', background: 'var(--dark-surface-2, #f3f5f8)' },
}

function hexToRgba(hex, alpha) {
	const value = parseInt(hex.slice(1), 16)
	return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}

function columnPoints(months, key, color) {
	return months.map(row => ({
		x: row.label,
		y: Number(row[key] || 0),
		fillColor: row.is_current ? hexToRgba(color, PARTIAL_OPACITY) : color,
	}))
}

/**
 * withLabels — значение над каждым столбцом, вертикально: двенадцать горизонтальных
 * подписей на узких столбцах налезают друг на друга. Над столбцом, а не внутри — внутри
 * у короткого столбца цифра не помещается и библиотека её прячет.
 */
function columnOptions(color, formatter, withLabels = false) {
	const options = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'bar' },
		colors: [color],
		plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: 'end', columnWidth: '55%' } },
		yaxis: {
			...baseChartOptions.yaxis,
			min: 0,
			forceNiceScale: true,
			labels: { ...baseChartOptions.yaxis.labels, formatter },
		},
		tooltip: { ...baseChartOptions.tooltip, y: { formatter } },
	}

	if (!withLabels) return options

	return {
		...options,
		plotOptions: {
			bar: {
				...options.plotOptions.bar,
				dataLabels: { position: 'top', orientation: 'vertical', hideOverflowingLabels: false },
			},
		},
		dataLabels: {
			enabled: true,
			formatter: value => formatNumber(value),
			offsetY: 5,
			style: { fontSize: '11px', fontWeight: 600, colors: ['#374151'] },
		},
		// Запас сверху, чтобы подпись самого высокого столбца не упёрлась в край
		yaxis: { ...options.yaxis, max: max => Math.ceil(max * 1.18) },
	}
}

/**
 * Пришли и ушли — две линии на одной оси (точки), значения подписаны прямо на точках:
 * в каком месяце ушедших больше, чем пришедших, видно сразу, без наведения — красная
 * линия встаёт над зелёной. Ушедших за текущий месяц ещё нет, их линия обрывается.
 */
function FlowChart({ months }) {
	const incoming = months.map(row => Number(row.connected || 0))
	const outgoing = months.map(row => (row.churned === null ? null : Number(row.churned || 0)))

	const series = [
		{ name: 'Пришли', data: incoming },
		{ name: 'Ушли', data: outgoing },
	]

	// Подписи — аннотациями, а не dataLabels: у dataLabels сдвиг один на все серии, и при
	// близких значениях цифры налезают друг на друга. Здесь в каждом месяце цифра верхней
	// линии стоит над точкой, нижней — под точкой, поэтому не пересекаются никогда.
	function pointLabel(x, y, color, isUpper) {
		return {
			x,
			y,
			marker: { size: 0 },
			label: {
				text: formatNumber(y),
				borderColor: color,
				borderRadius: 4,
				offsetY: isUpper ? -4 : 26,
				style: { background: color, color: '#fff', fontSize: '11px', fontWeight: 600, padding: { left: 5, right: 5, top: 2, bottom: 2 } },
			},
		}
	}

	const labels = []
	months.forEach((row, index) => {
		const inValue = incoming[index]
		const outValue = outgoing[index]
		const outOnTop = outValue !== null && outValue > inValue
		labels.push(pointLabel(row.label, inValue, COLOR_IN, !outOnTop))
		if (outValue !== null) {
			labels.push(pointLabel(row.label, outValue, COLOR_OUT, outOnTop))
		}
	})

	const options = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'line' },
		colors: [COLOR_IN, COLOR_OUT],
		stroke: { width: 2, curve: 'straight' },
		markers: { size: 4, strokeWidth: 2, strokeColors: '#fff', hover: { size: 6 } },
		annotations: { points: labels },
		// Запас сверху и снизу под подписи крайних точек
		grid: { ...baseChartOptions.grid, padding: { top: 16, bottom: 8 } },
		xaxis: { ...baseChartOptions.xaxis, categories: months.map(row => row.label) },
		yaxis: {
			...baseChartOptions.yaxis,
			min: 0,
			forceNiceScale: true,
			labels: { ...baseChartOptions.yaxis.labels, formatter: value => formatNumber(value) },
		},
		tooltip: {
			...baseChartOptions.tooltip,
			shared: true,
			intersect: false,
			y: { formatter: value => (value === null || value === undefined ? 'ещё не известно' : formatNumber(value)) },
		},
	}

	return <ReactApexChart options={options} series={series} type="line" height={320} />
}

function ChurnedStatus({ status }) {
	const label = STATUS_LABELS[status] || STATUS_LABELS[1]

	return (
		<span
			style={{
				color: label.color,
				background: label.background,
				borderRadius: '8px',
				padding: '2px 8px',
				fontWeight: 600,
				fontSize: '11px',
				whiteSpace: 'nowrap',
			}}
		>
			{label.text}
		</span>
	)
}

// Удержание по горстке точек — шум: 1 из 2 даёт 50 %, и агент краснеет зря
const RETENTION_MIN_COHORT = 5

const AGENT_COLUMNS = [
	{ key: 'working_points', title: 'Работают', hint: 'Были чеки за последние 30 дней' },
	{ key: 'connected', title: 'Пришли', hint: 'Подключены за период' },
	{ key: 'churned', title: 'Ушли', hint: 'Ушли за период: два месяца без чеков. Блокировка за неоплату уходом не считается' },
	{ key: 'never_started', title: 'Не запустились', hint: 'Подключены за период больше 30 дней назад и не пробили ни одного чека' },
	{ key: 'churned_fast', title: 'Ушли быстро', hint: 'Из ушедших за период — проработали не больше 3 месяцев, считая месяц подключения' },
	{ key: 'retention', title: 'Удержание', hint: 'Из подключённых за период (кроме последних 30 дней) сколько работает сейчас' },
]

function retentionClass(agent) {
	if (agent.retention === null || agent.cohort < RETENTION_MIN_COHORT) return ''
	if (agent.retention >= 70) return 'bg-success text-white'
	if (agent.retention >= 50) return 'bg-warning text-white'
	return 'bg-danger text-white'
}

function retentionTitle(agent) {
	if (agent.retention === null) return 'За период нет подключений старше 30 дней'
	const text = `Работают ${agent.cohort_working} из ${agent.cohort} подключённых`
	return agent.cohort < RETENTION_MIN_COHORT ? `${text} — слишком мало точек для вывода` : text
}

function agentLabel(agent) {
	return agent.name || agent.login || `#${agent.group_id}`
}

/** Ключ строки: итог ветки и строка агента — разные фильтры графиков (branch_id / agent_id) */
function agentRowKey(agent) {
	return agent.is_branch ? `branch:${agent.branch_id}` : `group:${agent.group_id}`
}

function sortValue(agent, key) {
	return agent[key] === null ? -1 : Number(agent[key])
}

/**
 * Строки приходят с бэка уже по порядку: итог ветки (is_branch), за ним её строки с level = 1.
 * Собираем в блоки, чтобы сортировка и сворачивание не разрывали ветку.
 */
function toBlocks(agents) {
	const blocks = []
	agents.forEach(agent => {
		if (agent.level === 1 && blocks.length > 0) {
			blocks[blocks.length - 1].children.push(agent)
		} else {
			blocks.push({ head: agent, children: [] })
		}
	})
	return blocks
}

/**
 * Пришли и ушли в разрезе агентов. В админке без выбранного партнёра — все ветки базы:
 * итог ветки, под ним свои точки партнёра и его агенты.
 *
 * Точка числится за одним агентом, поэтому «Итого» (сумма строк без итогов веток) равно
 * графику. Клик по строке сужает графики до её точек, повторный — снимает выбор.
 */
function AgentsTable({ agents, selectedKey, onSelect }) {
	const [sort, setSort] = useState(null)
	const [collapsed, setCollapsed] = useState({})

	const isPartnersMode = !agents.some(agent => agent.is_own && agent.level === 0)

	let blocks = toBlocks(agents)
	if (sort) {
		const byValue = (a, b) => sortValue(b, sort) - sortValue(a, sort)
		blocks = [...blocks]
			.sort((a, b) => byValue(a.head, b.head))
			.map(block => ({ ...block, children: [...block.children].sort(byValue) }))
	}

	const total = agents.filter(agent => !agent.is_branch).reduce((sum, agent) => {
		AGENT_COLUMNS.forEach(column => {
			if (column.key !== 'retention') sum[column.key] = (sum[column.key] || 0) + agent[column.key]
		})
		sum.cohort = (sum.cohort || 0) + agent.cohort
		sum.cohort_working = (sum.cohort_working || 0) + agent.cohort_working
		return sum
	}, {})
	total.retention = total.cohort > 0 ? Math.round(total.cohort_working * 1000 / total.cohort) / 10 : null

	function toggleBranch(event, branchId) {
		event.stopPropagation()
		setCollapsed({ ...collapsed, [branchId]: !collapsed[branchId] })
	}

	function renderValue(agent, key) {
		const value = agent[key]
		if (key === 'retention') return value === null ? '—' : `${String(value).replace('.', ',')} %`
		if (key === 'connected') return value > 0 ? `+${formatNumber(value)}` : '0'
		if (key === 'churned') return value > 0 ? `−${formatNumber(value)}` : '0'
		return formatNumber(value)
	}

	function valueStyle(agent, key) {
		const style = agent.is_branch ? { fontWeight: 700 } : {}
		if (key === 'connected' && agent.connected > 0) return { ...style, color: 'var(--dark-fg-success, #1f9d57)' }
		if (key === 'churned' && agent.churned > 0) return { ...style, color: 'var(--dark-fg-danger, #d23434)' }
		if (key === 'never_started' && agent.never_started > 0) return { color: 'var(--dark-fg-warning, #b7791f)', fontWeight: 600, ...style }
		if (key === 'churned_fast' && agent.churned_fast > 0) return { color: 'var(--dark-fg-danger, #d23434)', fontWeight: 600, ...style }
		return style
	}

	function renderRow(agent) {
		const key = agentRowKey(agent)
		const isSelected = key === selectedKey
		const isEmpty = agent.working_points === 0 && agent.connected === 0 && agent.churned === 0
		const isCollapsed = agent.is_branch && collapsed[agent.branch_id]

		let background
		if (isSelected) background = 'var(--dark-tint-primary, #eef3fe)'
		else if (agent.is_branch) background = 'var(--dark-surface-2, #f3f5f8)'

		return (
			<tr
				key={key}
				onClick={() => onSelect(isSelected ? null : agent)}
				style={{ cursor: 'pointer', opacity: isEmpty ? 0.55 : 1, background }}
			>
				<td
					style={{
						paddingLeft: agent.level === 1 ? '28px' : undefined,
						boxShadow: isSelected ? 'inset 3px 0 0 #4c8df0' : undefined,
					}}
				>
					<div className="d-flex align-items-start gap-1">
						{agent.is_branch && (
							<i
								className={isCollapsed ? 'uil-angle-right' : 'uil-angle-down'}
								title={isCollapsed ? 'Показать агентов' : 'Скрыть агентов'}
								style={{ fontSize: '16px', lineHeight: '18px' }}
								onClick={event => toggleBranch(event, agent.branch_id)}
							/>
						)}
						<div>
							<div style={{ fontWeight: agent.is_branch ? 700 : 600 }}>{agentLabel(agent)}</div>
							{agent.login && (
								<div style={mutedStyle}>
									{agent.login}
									{agent.is_branch ? ' · вся ветка' : ''}
								</div>
							)}
						</div>
					</div>
				</td>
				{AGENT_COLUMNS.map(column => (
					column.key === 'retention'
						? (
							<td
								key={column.key}
								className={`text-center align-middle ${retentionClass(agent)}`}
								style={agent.is_branch ? { fontWeight: 700 } : undefined}
								title={retentionTitle(agent)}
							>
								{renderValue(agent, column.key)}
							</td>
						)
						: (
							<td key={column.key} className="text-center align-middle" style={valueStyle(agent, column.key)}>
								{renderValue(agent, column.key)}
							</td>
						)
				))}
			</tr>
		)
	}

	return (
		<Card
			title={isPartnersMode ? 'По партнёрам' : 'По агентам'}
			right={<span style={mutedStyle}>нажмите на строку — графики ниже покажут только её точки</span>}
		>
			<div className="table-responsive" style={{ maxHeight: '520px', overflowY: 'auto' }}>
				<table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
					<thead>
						<tr>
							<th>{isPartnersMode ? 'Партнёр' : 'Агент'}</th>
							{AGENT_COLUMNS.map(column => (
								<th
									key={column.key}
									className="text-center"
									title={column.hint}
									style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
									onClick={() => setSort(sort === column.key ? null : column.key)}
								>
									{column.title}
									{sort === column.key ? ' ↓' : ''}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{blocks.map(block => (
							<React.Fragment key={agentRowKey(block.head)}>
								{renderRow(block.head)}
								{!collapsed[block.head.branch_id] && block.children.map(renderRow)}
							</React.Fragment>
						))}
					</tbody>
					<tfoot>
						<tr style={{ fontWeight: 700 }}>
							<td>Итого</td>
							{AGENT_COLUMNS.map(column => (
								<td
									key={column.key}
									className="text-center"
									title={column.key === 'retention' ? retentionTitle(total) : undefined}
								>
									{renderValue(total, column.key)}
								</td>
							))}
						</tr>
					</tfoot>
				</table>
			</div>
			<div className="mt-2" style={mutedStyle}>
				Точка числится за одним агентом: за нижним в цепочке, а если агентов двое — за тем, у кого доля больше.
				Точки субагентов входят в строку их агента. Удержание по выборке меньше {RETENTION_MIN_COHORT} точек не окрашивается.
			</div>
		</Card>
	)
}

/**
 * Блок общий для партнёрского кабинета и админки (вкладка «Пришли и ушли»): расчёт на
 * бэке тоже общий — PointFlowService, — поэтому цифры у партнёра и у нас совпадают.
 *
 * url      — эндпоинт: /partner/analytics/monthly или /admin/analytics/monthly
 * params   — доп. фильтры запроса (в админке — partner_id)
 * filters  — разметка фильтров рядом с переключателем периода
 * withSummary — плитки «работают / активны без чеков» (у партнёра они в шапке страницы)
 */
function AnalyticsMonthly({ url = '/partner/analytics/monthly', params = {}, filters = null, withSummary = false }) {
	const [months, setMonths] = useState(12)
	const [loader, setLoader] = useState(true)
	const [data, setData] = useState({ months: [], churned_points: [], agents: [] })
	// Строка таблицы агентов, по которой сужены графики
	const [agent, setAgent] = useState(null)

	// Пользователь щёлкает период и партнёра быстрее, чем отвечает сервер, —
	// принимаем только ответ последнего запроса
	const requestId = useRef(0)
	const paramsKey = JSON.stringify(params)
	const agentKey = agent ? agentRowKey(agent) : null

	async function getData(period) {
		const id = ++requestId.current
		setLoader(true)
		const query = { ...params, months: period }
		// Итог ветки — вся ветка партнёра, остальные строки — только свои точки
		if (agent && agent.is_branch) query.branch_id = agent.branch_id
		else if (agent) query.agent_id = agent.group_id
		const response = await GET(url, query)
		if (id !== requestId.current) return
		if (httpOk(response)) {
			setData(response.data)
		}
		setLoader(false)
	}

	// В админке сменили партнёра — строки агентов уже другие
	useEffect(() => {
		setAgent(null)
	}, [url, paramsKey])

	useEffect(() => {
		getData(months)
	}, [months, url, paramsKey, agentKey]) // eslint-disable-line react-hooks/exhaustive-deps

	const summary = data.summary || {}

	const rows = data.months || []
	const churnedPoints = data.churned_points || []
	const agents = data.agents || []
	const labelByMonth = Object.fromEntries(rows.map(row => [row.month, row.label]))

	const totalIn = rows.reduce((sum, row) => sum + Number(row.connected || 0), 0)
	const totalOut = rows.reduce((sum, row) => sum + Number(row.churned || 0), 0)

	const periodSwitch = (
		<div className="btn-group btn-group-sm">
			{PERIODS.map(period => (
				<button
					key={period}
					type="button"
					className={`btn ${months === period ? 'btn-primary' : 'btn-outline-primary'}`}
					onClick={() => setMonths(period)}
				>
					{period} мес.
				</button>
			))}
		</div>
	)

	return (
		<div className="mb-3">
			<div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-2">
				<div>
					<h5 className="mb-1">Динамика по месяцам</h5>
					<div style={mutedStyle}>
						Точки считаются работающими, только если пробивали чеки. Текущий месяц неполный — его столбец бледнее.
					</div>
				</div>
				<div className="d-flex flex-wrap align-items-center gap-2">
					{agent && (
						<button
							type="button"
							className="btn btn-sm btn-outline-primary"
							title="Показать снова все точки"
							onClick={() => setAgent(null)}
						>
							{agentLabel(agent)} <i className="uil-times" />
						</button>
					)}
					{filters}
					{periodSwitch}
				</div>
			</div>

			{withSummary && !loader && data.summary && (
				<div className="row g-3 mb-3">
					<div className="col-md-4">
						<Card title="Работают">
							<div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--dark-fg-success, #1f9d57)' }}>
								{formatNumber(summary.working_points)}
							</div>
							<div style={mutedStyle}>были чеки за последние 30 дней</div>
						</Card>
					</div>
					<div className="col-md-4">
						<Card title="Активны, но без чеков">
							<div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--dark-fg-danger, #d23434)' }}>
								{formatNumber(summary.idle_points)}
							</div>
							<div style={mutedStyle}>статус «активна», но за 30 дней ни одного чека — в работающие не идут</div>
						</Card>
					</div>
					<div className="col-md-4">
						<Card title="Активны по статусу">
							<div style={{ fontSize: '26px', fontWeight: 800, color: 'var(--dark-text-strong, #111827)' }}>
								{formatNumber(summary.active_points)}
							</div>
							<div style={mutedStyle}>для сравнения: сколько точек числится активными</div>
						</Card>
					</div>
				</div>
			)}

			{/* Таблица живёт поверх загрузки: при выборе строки меняются только графики */}
			{agents.length > 1 && (
				<div className="mb-3">
					<AgentsTable agents={agents} selectedKey={agentKey} onSelect={setAgent} />
				</div>
			)}

			{loader && (
				<div className="row g-3">
					<div className="col-lg-8"><Skeleton height={340} /></div>
					<div className="col-lg-4"><Skeleton height={340} /></div>
				</div>
			)}

			{!loader && rows.length === 0 && (
				<Card><EmptyNote>Нет данных</EmptyNote></Card>
			)}

			{!loader && rows.length > 0 && (
				<>
					<div className="row g-3 mb-3">
						<div className="col-lg-8">
							<Card
								title="Пришли и ушли"
								right={(
									<span style={mutedStyle}>
										за период: <b style={{ color: 'var(--dark-fg-success, #1f9d57)' }}>+{formatNumber(totalIn)}</b>
										{' / '}
										<b style={{ color: 'var(--dark-fg-danger, #d23434)' }}>−{formatNumber(totalOut)}</b>
									</span>
								)}
							>
								<FlowChart months={rows} />
								<div style={mutedStyle}>
									Ушла — точка без единого чека два месяца подряд, месяц ухода — первый месяц без чеков.
									Блокировка за неоплату уходом не считается. За текущий месяц уход ещё не известен.
								</div>
							</Card>
						</div>
						<div className="col-lg-4">
							<Card title="Работающие точки">
								<ReactApexChart
									options={columnOptions(COLOR_MAIN, value => formatNumber(value), true)}
									series={[{ name: 'Работали', data: columnPoints(rows, 'working_points', COLOR_MAIN) }]}
									type="bar"
									height={300}
								/>
								<div style={mutedStyle}>Точки, у которых за месяц был хотя бы один чек</div>
							</Card>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-lg-4">
							<Card title="Оборот клиентов">
								<ReactApexChart
									options={columnOptions(COLOR_MAIN, value => formatAmount(value))}
									series={[{ name: 'Оборот', data: columnPoints(rows, 'gtv', COLOR_MAIN) }]}
									type="bar"
									height={260}
								/>
								<div style={mutedStyle}>Продажи за вычетом возвратов</div>
							</Card>
						</div>
						<div className="col-lg-4">
							<Card title="Количество чеков">
								<ReactApexChart
									options={columnOptions(COLOR_MAIN, value => formatNumber(value))}
									series={[{ name: 'Чеков', data: columnPoints(rows, 'cheques', COLOR_MAIN) }]}
									type="bar"
									height={260}
								/>
							</Card>
						</div>
						<div className="col-lg-4">
							<Card title="Средний чек">
								<ReactApexChart
									options={{
										...baseChartOptions,
										chart: { ...baseChartOptions.chart, type: 'line' },
										colors: [COLOR_MAIN],
										stroke: { width: 2, curve: 'smooth' },
										markers: { size: 4, strokeWidth: 2, strokeColors: '#fff' },
										xaxis: { ...baseChartOptions.xaxis, categories: rows.map(row => row.label) },
										yaxis: {
											...baseChartOptions.yaxis,
											forceNiceScale: true,
											labels: { ...baseChartOptions.yaxis.labels, formatter: value => formatAmount(value) },
										},
										tooltip: { ...baseChartOptions.tooltip, y: { formatter: value => formatNumber(value) } },
									}}
									series={[{ name: 'Средний чек', data: rows.map(row => Math.round(row.avg_cheque || 0)) }]}
									type="line"
									height={260}
								/>
							</Card>
						</div>
					</div>

					<div className="row g-3">
						<div className="col-lg-7">
							<Card title="По месяцам">
								<div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
									<table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
										<thead>
											<tr>
												<th>Месяц</th>
												<th className="text-end">Пришли</th>
												<th className="text-end">Ушли</th>
												<th className="text-end">Работали</th>
												<th className="text-end">Оборот</th>
												<th className="text-end">Чеков</th>
												<th className="text-end">Средний чек</th>
											</tr>
										</thead>
										<tbody>
											{[...rows].reverse().map(row => (
												<tr key={row.month}>
													<td>
														{row.label}
														{row.is_current && <span style={mutedStyle}> · идёт</span>}
													</td>
													<td className="text-end" style={{ color: 'var(--dark-fg-success, #1f9d57)' }}>
														{row.connected > 0 ? `+${formatNumber(row.connected)}` : '0'}
													</td>
													<td className="text-end" style={{ color: 'var(--dark-fg-danger, #d23434)' }}>
														{row.churned === null ? '—' : (row.churned > 0 ? `−${formatNumber(row.churned)}` : '0')}
													</td>
													<td className="text-end">{formatNumber(row.working_points)}</td>
													<td className="text-end">{formatAmount(row.gtv)}</td>
													<td className="text-end">{formatNumber(row.cheques)}</td>
													<td className="text-end">{formatNumber(row.avg_cheque)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</Card>
						</div>
						<div className="col-lg-5">
							<Card
								title="Кто ушёл"
								right={<span style={mutedStyle}>всего: {formatNumber(churnedPoints.length)}</span>}
							>
								{churnedPoints.length === 0 && <EmptyNote>За период никто не ушёл</EmptyNote>}
								{churnedPoints.length > 0 && (
									<div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
										<table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
											<thead>
												<tr>
													<th>Точка</th>
													<th>Ушла</th>
													<th>Последний чек</th>
												</tr>
											</thead>
											<tbody>
												{churnedPoints.map(point => (
													<tr key={point.pos_id}>
														<td>
															<div style={{ fontWeight: 600 }}>{point.name}</div>
															<div style={mutedStyle}>
																{point.login}
																{point.agent_name ? ` · ${point.agent_name}` : ''}
															</div>
														</td>
														<td>
															<div>{labelByMonth[point.churn_month] || point.churn_month}</div>
															<ChurnedStatus status={point.status} />
														</td>
														<td style={{ whiteSpace: 'nowrap' }}>
															{point.last_cheque_at ? formatDate(point.last_cheque_at, 'dd.MM.yyyy') : '—'}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</Card>
						</div>
					</div>
				</>
			)}
		</div>
	)
}

export default AnalyticsMonthly
